import type { FormEvent } from 'react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';

import { Button } from '@/lib/base/buttons/button';
import { HintText, useFormValidation } from '@/lib/base/forms';
import { Input } from '@/lib/base/forms/input/input';
import { LoadingIndicator } from '@/lib/base/foundations/loading-indicator';
import { fetchWithDefaults } from '@/lib/http/client';
import { logger } from '@/lib/logger';

const CODE_FIELDS = { code: { required: true } } as const;

export const SignupCode = () => {
  const [code, setCode] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [isValidating, setIsValidating] = useState(false);
  const { getFieldProps, touchAll, isValid } = useFormValidation({
    fields: CODE_FIELDS,
    values: { code },
  });
  const codeField = getFieldProps('code');
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  // When the code arrives via the URL (a shared /signup/code?code=… invite
  // link), we validate it on mount and skip straight to signup. Seed this
  // synchronously from the URL so the code form never renders — and therefore
  // never flashes — before the redirect on that path. Only a non-blank code
  // seeds the verifying state: a present-but-empty/whitespace param (e.g.
  // ?code=%20) has nothing to validate, so the form is shown immediately
  // instead of dead-ending on the verifying spinner. It flips false once the
  // user interacts with the form (see onSubmit), so a URL code that fails
  // validation reveals the form and behaves normally from then on.
  const [autoValidating, setAutoValidating] = useState(() =>
    Boolean(searchParams.get('code')?.trim())
  );
  // Guards the URL-driven auto-submit so it fires at most once, and never
  // competes with the user typing into the field.
  const autoSubmittedRef = useRef(false);
  // Guards async state updates against setting state after unmount (the
  // URL-driven auto-validate flow can outlive the component, e.g. if the user
  // navigates away while the request is in flight).
  const isMountedRef = useRef(true);

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  // Validate an invitation code against the API and route onward. Shared by the
  // form submit and the URL-driven auto-submit so both paths behave identically.
  const validateAndProceed = useCallback(
    async (codeValue: string) => {
      const trimmed = codeValue.trim();
      if (!trimmed) {
        return;
      }
      setError(null);
      setIsValidating(true);

      try {
        // Validate the invitation code via API
        const response = await fetchWithDefaults('/api/waitlist/validate/', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ code: trimmed }),
        });

        if (!response.ok) {
          // Try to parse error response
          const errorData = await response.json();
          logger.debug('Error response', { errorData });

          // Handle different error response formats
          const errorMessage =
            errorData.message ||
            (errorData.code && Array.isArray(errorData.code)
              ? errorData.code[0]
              : null) ||
            'Invalid invitation code. Please check and try again.';

          if (isMountedRef.current) {
            setError(errorMessage);
          }
          return;
        }

        const data = await response.json();

        if (data.valid) {
          // Check if we should complete OAuth signup
          if (data.redirect_to_oauth) {
            // Complete OAuth signup using stored OAuth data
            try {
              const completeResponse = await fetchWithDefaults(
                '/api/waitlist/complete-oauth-signup/',
                {
                  method: 'POST',
                  headers: {
                    'Content-Type': 'application/json',
                  },
                }
              );

              if (!completeResponse.ok) {
                const errorData = await completeResponse.json();
                if (isMountedRef.current) {
                  setError(
                    errorData.error ||
                      "We couldn't complete your signup. Try again."
                  );
                }
                return;
              }

              const completeData = await completeResponse.json();

              // Redirect to wherever the backend tells us
              if (completeData.redirect_url) {
                window.location.href = completeData.redirect_url;
              } else {
                // Fallback to home (HomeRedirect will route appropriately)
                window.location.href = '/';
              }
            } catch (err: unknown) {
              console.error('Complete OAuth signup error:', err);
              if (isMountedRef.current) {
                setError(
                  'An error occurred while completing signup. Please try again.'
                );
              }
            }
          } else {
            // Store the code and redirect to signup
            navigate(`/account/signup?code=${encodeURIComponent(trimmed)}`);
          }
        } else {
          // Show the error message from the API
          if (isMountedRef.current) {
            setError(
              data.message ||
                'Invalid invitation code. Please check and try again.'
            );
          }
        }
      } catch (err: unknown) {
        console.error('Validation error:', err);
        if (isMountedRef.current) {
          setError(
            'An error occurred while validating your code. Please try again.'
          );
        }
      } finally {
        if (isMountedRef.current) {
          setIsValidating(false);
        }
      }
    },
    [navigate]
  );

  // Pre-fill code from URL parameter if present. When a code is supplied via
  // the URL (a shared /signup/code?code=… invite link), also auto-validate it
  // once so the recipient skips the manual "Continue" click. An invalid or
  // expired code fails validation and leaves the prefilled screen showing the
  // error so they can correct it.
  useEffect(() => {
    const codeParam = searchParams.get('code');
    const demoParam = searchParams.get('demo');
    // A URL code and demo mode each kick off their own validate+navigate flow.
    // Running both at once races to different /account/signup destinations, so
    // they are mutually exclusive: a concrete invite code takes precedence, and
    // demo mode is only considered when there is no usable code in the URL.
    const hasUsableCode = Boolean(codeParam?.trim());

    if (codeParam?.trim()) {
      setCode(codeParam);
      if (!autoSubmittedRef.current) {
        autoSubmittedRef.current = true;
        void validateAndProceed(codeParam);
      }
    } else if (codeParam !== null) {
      // Present but blank/whitespace-only: there is nothing to validate, so
      // reveal the form instead of leaving autoValidating stuck true behind the
      // verifying spinner.
      setAutoValidating(false);
    }

    // Check for demo mode — skipped when a usable code is present, so the code
    // and demo flows never race (see the note above).
    if (!hasUsableCode && demoParam === 'true') {
      // Set demo mode in session before navigating to signup
      const enableDemoMode = async () => {
        try {
          const response = await fetchWithDefaults('/api/waitlist/validate/', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({ demo: true }),
          });

          if (response.ok) {
            // Set demo flag in sessionStorage for onboarding to detect
            sessionStorage.setItem('signupDemo', 'true');
            // Demo mode set in session, now navigate to signup
            navigate('/account/signup?demo=true');
          } else {
            const errorData = await response.json();
            if (isMountedRef.current) {
              setError(
                errorData.message ||
                  'Demo mode is not enabled. Please contact support.'
              );
            }
          }
        } catch (err) {
          console.error('Error enabling demo mode:', err);
          if (isMountedRef.current) {
            setError(
              'An error occurred while enabling demo mode. Please try again.'
            );
          }
        }
      };

      enableDemoMode();
    }

    // Handle error redirects from OAuth flow
    const errorParam = searchParams.get('error');
    if (errorParam === 'invite_required') {
      setError(
        'An invitation code is required to create an account. Please enter your code below.'
      );
    } else if (errorParam === 'invalid_code') {
      setError(
        'Your invitation code has expired or is invalid. Please enter a valid code.'
      );
    }
  }, [searchParams, navigate, validateAndProceed]);

  const onSubmit = useCallback(
    async (e: FormEvent<HTMLFormElement>) => {
      e.preventDefault();
      // The user is driving the form directly now; never fall back to the
      // URL-driven verifying state (which would hide the form mid-interaction).
      setAutoValidating(false);
      setError(null);
      touchAll();
      if (!isValid) {
        return;
      }
      await validateAndProceed(code);
    },
    [touchAll, isValid, code, validateAndProceed]
  );

  // A code supplied via the URL is validated on mount (see the effect above).
  // While that first validation is in flight — and before any error surfaces —
  // show a lightweight verifying state instead of the form, so the recipient of
  // a shared invite link never sees the code screen flash before the redirect.
  // A valid code redirects (this stays visible until unmount); an invalid one
  // sets an error, which reveals the form below.
  if (autoValidating && !error) {
    return (
      <div className='flex flex-col gap-8 text-center'>
        <div className='flex flex-col items-center gap-4'>
          <LoadingIndicator size='md' />
          <p className='text-base text-gray-600 dark:text-gray-400'>
            Verifying your invitation…
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className='flex flex-col gap-8'>
      {/* Header */}
      <div className='flex flex-col gap-3'>
        <h1 className='text-3xl font-semibold text-fg-brand-primary md:text-4xl'>
          Enter your invitation code
        </h1>
        <p className='text-base text-gray-600 dark:text-gray-400'>
          Ahoy workspaces are set up with your team during onboarding. Enter the
          invitation code from your welcome email to sign up with Google.
        </p>
      </div>

      <form onSubmit={onSubmit} noValidate className='flex flex-col gap-6'>
        {/* Global error */}
        {error && (
          <div className='rounded-lg bg-red-50 dark:bg-red-900/10 p-4 border border-red-200 dark:border-red-800'>
            <p className='text-sm text-red-800 dark:text-red-200'>{error}</p>
          </div>
        )}

        <div>
          <label
            className='block text-sm font-medium text-gray-700 dark:text-gray-200 mb-1.5'
            htmlFor='code'
          >
            Invitation code
          </label>
          <Input
            type='text'
            id='code'
            placeholder='Enter your invitation code'
            isRequired
            onChange={setCode}
            value={code}
            name='code'
            className='font-mono text-center text-lg tracking-wider'
            autoFocus
            onBlur={codeField.onBlur}
            isInvalid={codeField.showError}
            showError={codeField.showError}
          />
          {codeField.showError && codeField.error && (
            <HintText isInvalid showError className='mt-1.5'>
              {codeField.error}
            </HintText>
          )}
          <p className='mt-1.5 text-sm text-gray-500 dark:text-gray-400'>
            Check your email for your invitation code
          </p>
        </div>

        <div className='flex flex-col gap-4'>
          <Button
            type='submit'
            disabled={isValidating}
            className='w-full'
            isLoading={isValidating}
            showTextWhileLoading
          >
            {isValidating ? 'Validating…' : 'Continue'}
          </Button>
        </div>
      </form>

      {/* Demo Link. Ahoy is open to new customers with demo-led onboarding;
          there is no waitlist, so send code-less visitors to book a demo. */}
      <div className='flex flex-col gap-3 text-center'>
        <div>
          <p className='text-sm text-gray-600 dark:text-gray-400 mb-2'>
            New to Ahoy? We&apos;ll set up your workspace together.
          </p>
          <a
            href='https://meet.ahoy.ai/ahoy-team'
            target='_blank'
            rel='noopener noreferrer'
            className='text-sm font-semibold text-fg-brand-primary hover:text-utility-brand-700 transition-colors'
          >
            Book a demo
          </a>
        </div>

        {/* Sign In Link */}
        <div className='flex justify-center gap-1'>
          <span className='text-sm text-gray-600 dark:text-gray-400'>
            Already have an account?
          </span>
          <Link
            to='/account/login'
            className='text-sm font-semibold text-fg-brand-primary hover:text-utility-brand-700 transition-colors'
          >
            Log in
          </Link>
        </div>
      </div>
    </div>
  );
};
